import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Divider,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import "./NordicDashboard.css";
import {
  countries,
  countryList,
  stages,
  stageList,
  multicenter,
  multicenterList,
  enrollmentList,
  enrollment,
  phaseList,
  phase,
  timeThreshold,
  registry,
  registryList,
} from "./constants";
import { reverseMapSponsors } from "./sponsorMappings";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { DataGrid } from "@mui/x-data-grid";
import IntroductionAccordion from "../IntroductionAccordion";
import Inputs from "../Inputs";

const datasetCache = {};

const getCachedDataset = async (url) => {
  // Use in-memory cache first
  if (datasetCache[url]) {
    return datasetCache[url];
  }

  // // Try localStorage
  // const cached = localStorage.getItem(url);
  // if (cached) {
  //   const parsed = JSON.parse(cached);
  //   datasetCache[url] = parsed;
  //   return parsed;
  // }

  // Otherwise, fetch from network
  const response = await fetch(url);
  const data = await response.json();
  datasetCache[url] = data;
  // localStorage.setItem(url, JSON.stringify(data));
  return data;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const createOptions = (totalTrials) => ({
  responsive: true,
  maintainAspectRatio: false,

  indexAxis: "y",
  plugins: {
    legend: {
      position: "bottom",
      display: true,
      labels: {
        font: {
          size: 16,
          weight: "bold",
        },
        color: "#0f172a",
      },
    },
    title: {
      display: true,
      text: `Clinical trial results reporting by sponsor (${totalTrials} trials)`,
      font: {
        size: 20,
        weight: "bold",
        family: "Roboto",
      },
      color: "#0f172a",
    },
    tooltip: {
      titleFont: {
        size: 14,
        weight: "bold",
      },
      bodyFont: {
        size: 14,
        weight: "normal",
      },
      callbacks: {
        title: (tooltipItems) => {
          const shortName = tooltipItems[0].label;
          const fullName = reverseMapSponsors[shortName] || shortName;
          return fullName;
        },
        label: (obj, i) => {
          const n = obj?.dataset?.extra[obj?.dataIndex]?.value?.n;
          const timelyN = obj?.dataset?.extra[obj?.dataIndex]?.value?.timelyN;
          return `${
            Math.round(obj.raw * 1000) / 10
          }%, ${timelyN}/${n} reported`;
        },
      },
    },
  },
  scales: {
    y: {
      ticks: {
        autoSkip: false,
        font: {
          size: 16,
          family: "Roboto",
          // weight: "bold",
        },
        color: "#0f172a",
      },
    },
    x: {
      position: "top",
      ticks: {
        // Include a percentage sign in the ticks
        callback: (value) => Math.round(value * 100) + "%",
        font: {
          size: 16,
          family: "Roboto",
          // weight: "bold",
        },
        color: "#0f172a",
      },
      suggestedMin: 0,
      suggestedMax: 1,
    },
  },
});

const createStackedOptions = (totalTrials) => ({
  responsive: true,
  maintainAspectRatio: false,

  indexAxis: "y",
  plugins: {
    legend: {
      position: "bottom",
      display: true,
      labels: {
        font: {
          size: 16,
          weight: "bold",
        },
        color: "#0f172a",
      },
    },
    title: {
      display: true,
      text: `Clinical trial results reporting by sponsor (${totalTrials} trials)`,
      font: {
        size: 20,
        weight: "bold",
        family: "Roboto",
      },
      color: "#0f172a",
    },
    tooltip: {
      titleFont: {
        size: 14,
        weight: "bold",
      },
      bodyFont: {
        size: 14,
        weight: "normal",
      },
      callbacks: {
        title: (tooltipItems) => {
          const shortName = tooltipItems[0].label;
          const fullName = reverseMapSponsors[shortName] || shortName;
          return fullName;
        },
        label: (obj) => {
          return `${obj.dataset.label}=${obj.raw}`;
        },
      },
    },
  },
  scales: {
    y: {
      stacked: true,
      ticks: {
        autoSkip: false,
        font: {
          size: 16,
          family: "Roboto",
        },
        color: "#0f172a",
      },
    },
    x: {
      position: "top",
      stacked: true,
      ticks: {
        font: {
          size: 16,
          family: "Roboto",
        },
        color: "#0f172a",
      },
    },
  },
});

// Filter the trial data based on the selections made by user. Passed down through the "filter" property
const filterTrialData = (filters, trialData) => {
  const {
    country,
    isMulticenter,
    selectedEnrollment,
    selectedPhase,
    selectedRegistry,
  } = filters;
  const filteredCountry = trialData.filter((trial) => {
    const includedCountry =
      country === countries.ALL || country === trial.lead_country;

    return includedCountry;
  });

  const filteredMulticenter =
    isMulticenter === multicenter.ALL
      ? filteredCountry
      : filteredCountry.filter(
          (trial) => trial.is_multicentric === isMulticenter
        );

  // Comparison on label due to object comparison perhaps being unreliable..
  const filteredEnrollment =
    selectedEnrollment.label === enrollment.ALL.label
      ? filteredMulticenter
      : filteredMulticenter.filter(
          (trial) =>
            trial.enrollment >= selectedEnrollment.lower &&
            trial.enrollment <= selectedEnrollment.upper
        );

  const filteredPhase =
    selectedPhase === phase.ALL
      ? filteredEnrollment
      : filteredEnrollment.filter(
          (trial) => trial.harmonizedPhase === selectedPhase
        );

  const filteredRegistry =
    selectedRegistry.label === registry.ALL.label
      ? filteredPhase
      : filteredPhase.filter(
          (trial) => trial.registry === selectedRegistry.value
        );

  return filteredRegistry;
};

// Add property isTimely that determines if trial is to be counted as timely reported,
// Base this property upon the values entered by the user ("timelyFilters").
const addTimelyProp = (filteredData, timelyFilters) => {
  const { daysCutOff, stageIdentified } = timelyFilters;
  return filteredData.map((trial) => {
    const lowestDays = Math.min(
      trial.days_cd_to_publication || Number.MAX_SAFE_INTEGER,
      trial.days_cd_to_summary || Number.MAX_SAFE_INTEGER
    );
    console.log({
      trial,
      lowestDays,
      cdpub: trial.days_cd_to_publication,
      cdsum: trial.days_cd_to_summary,
    });
    const isTimely =
      stageIdentified.value === stages.ALL.value
        ? daysCutOff >= lowestDays
        : daysCutOff >= lowestDays &&
          trial.identification_step === stageIdentified.value;
    return { ...trial, isTimely };
  });
};

// Create the data for the bar chart to be visualized
const createChartData = (timelyData, theme) => {
  // Determine which institutions have data to be reported
  const institutions = timelyData.reduce((prev, cur) => {
    prev[cur.harmonizedSponsor] = {
      n: 0,
      timelyN: 0,
    };
    return prev;
  }, {});

  // For each trial, add 1 to the counter of each institutions number of trials.
  // Also add 1 to the timely counter if the trial is considered timely reported
  timelyData.forEach((trial) => {
    const institution = trial.harmonizedSponsor;
    institutions[institution].n += 1;
    institutions[institution].timelyN += trial.isTimely ? 1 : 0;
  });

  // Go through each institution and compute the fraction of timely trials
  const unsortedData = Object.values(institutions).map(({ timelyN, n }) => ({
    percentage: timelyN / n,
    n,
    timelyN,
  }));
  const labels = Object.keys(institutions);

  // Merge them back together for sorting
  const mergedUnsorted = labels.map((label, index) => {
    return {
      label,
      value: unsortedData[index],
    };
  });

  // Sort them
  const sortedData = mergedUnsorted.sort(
    (a, b) => b.value?.percentage - a.value?.percentage
  );

  return {
    labels: sortedData.map(({ label }) => label),
    datasets: [
      {
        data: sortedData.map(({ value }) => value.percentage),
        extra: sortedData,
        backgroundColor: theme.palette.accent.main,
        label: "Percentage reported",
      },
    ],
  };
};

// Create the data for the bar chart to be visualized
const createStackedChartData = (timelyData, theme) => {
  // Determine which institutions have data to be reported
  const institutions = timelyData.reduce((prev, cur) => {
    prev[cur.harmonizedSponsor] = {
      n: 0,
      timelyN: 0,
    };
    return prev;
  }, {});

  // For each trial, add 1 to the counter of each institutions number of trials.
  // Also add 1 to the timely counter if the trial is considered timely reported
  timelyData.forEach((trial) => {
    const institution = trial.harmonizedSponsor;
    institutions[institution].n += 1;
    institutions[institution].timelyN += trial.isTimely ? 1 : 0;
  });

  // Go through each institution and compute the fraction of timely trials
  const unsortedData = Object.values(institutions).map(({ timelyN, n }) => {
    return { timelyN, untimelyN: n - timelyN, n };
  });

  const labels = Object.keys(institutions);

  // Merge them back together for sorting
  const mergedUnsorted = labels.map((label, index) => {
    return {
      label,
      value: unsortedData[index],
    };
  });

  // Sort them
  const sortedData = mergedUnsorted.sort((a, b) => b.value?.n - a.value?.n);

  return {
    labels: sortedData.map(({ label }) => label),
    datasets: [
      {
        data: sortedData.map(({ value }) => value?.timelyN),
        backgroundColor: theme.palette.accent.main,
        label: "N reported",
      },
      {
        data: sortedData.map(({ value }) => value?.untimelyN),
        backgroundColor: theme.palette.primary.main,
        label: "N unreported",
      },
    ],
  };
};

// Create the data for the bar chart to be visualized
const createTableData = (timelyData) => {
  // Determine which institutions have data to be reported
  const institutions = timelyData.reduce((prev, cur) => {
    prev[cur.singleSponsorSplit] = {
      n: 0,
      timelyN: 0,
    };
    return prev;
  }, {});

  // For each trial, add 1 to the counter of each institutions number of trials.
  // Also add 1 to the timely counter if the trial is considered timely reported
  timelyData.forEach((trial) => {
    const institution = trial.singleSponsorSplit;
    institutions[institution].n += 1;
    institutions[institution].timelyN += trial.isTimely ? 1 : 0;
  });

  // Go through each institution and compute the fraction of timely trials
  const unsortedData = Object.entries(institutions).map(([key, value]) => {
    const name = key;
    const n = value.n;
    const timelyN = value.timelyN;
    const fraction = timelyN / n;
    const percent = fraction * 100;
    const percentRounded = Math.round(percent * 10) / 10;
    return {
      id: name,
      name,
      n,
      timelyN,
      fraction,
      percentRounded,
    };
  });

  // Sort them
  const sortedData = unsortedData.sort((a, b) => b.fraction - a.fraction);

  return sortedData;
};

const datasetTimePairings = [
  {
    url: "/data/trialDataHarmonized12Months.json",
    threshold: timeThreshold.MONTHS_12,
  },
  {
    url: "/data/trialDataHarmonized24Months.json",
    threshold: timeThreshold.MONTHS_24,
  },
  {
    url: "/data/trialDataHarmonized36Months.json",
    threshold: timeThreshold.MONTHS_36,
  },
  {
    url: "/data/trialDataHarmonized48Months.json",
    threshold: timeThreshold.MONTHS_48,
  },
  {
    url: "/data/trialDataHarmonized60Months.json",
    threshold: timeThreshold.MONTHS_60,
  },
  {
    url: "/data/trialDataHarmonized0Months.json",
    threshold: timeThreshold.ALL,
  },
];
const dataGridColumns = [
  { field: "name", headerName: "Name", width: 260 },
  { field: "n", headerName: "Studies (N)", width: 120, type: "number" },
  {
    field: "timelyN",
    headerName: "Reported studies (N)",
    width: 160,
    type: "number",
  },
  {
    field: "percentRounded",
    headerName: "Percentage Reported",
    width: 160,
    type: "number",
    valueFormatter: (params) => {
      return `${params} %`;
    },
  },
];

function NordicDashboard() {
  const theme = useTheme();
  const [selectedPairing, setSelectedPairing] = useState(
    datasetTimePairings[0]
  );
  const [country, setCountry] = useState(countryList[0]);
  const [stageIdentified, setStageIdentified] = useState(stageList[0]);
  const [isMulticenter, setIsMulticenter] = useState(multicenterList[0]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(
    enrollmentList[0]
  );
  const [selectedPhase, setSelectedPhase] = useState(phaseList[0]);
  const [selectedRegistry, setSelectedRegistry] = useState(registryList[0]);

  const tabs = ["Percentages", "Absolute Values", "Table"];
  const [currentTab, setCurrentTab] = React.useState(tabs[0]);

  const [chartData, setChartData] = useState({ datasets: [], labels: [] });
  const [stackedChartData, setStackedChartData] = useState({
    datasets: [],
    labels: [],
  });
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalTrials, setTotalTrials] = useState(0);

  useEffect(() => {
    const fetchAndProcessData = async () => {
      try {
        setIsLoading(true);
        const rawData = await getCachedDataset(selectedPairing.url);

        const filteredData = filterTrialData(
          {
            country,
            isMulticenter,
            selectedEnrollment,
            selectedPhase,
            selectedRegistry,
          },
          rawData
        );

        const timelyData = addTimelyProp(filteredData, {
          daysCutOff: selectedPairing.threshold.value,
          stageIdentified,
        });

        setChartData(createChartData(timelyData, theme));
        setStackedChartData(createStackedChartData(timelyData, theme));
        setTableData(createTableData(timelyData));
        setTotalTrials(timelyData.length);
      } catch (error) {
        console.error("Failed to load dataset:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessData();
  }, [
    country,
    stageIdentified,
    isMulticenter,
    selectedEnrollment,
    selectedPhase,
    selectedPairing,
    selectedRegistry,
  ]);

  return (
    <React.Fragment>
      {/* <TitleBox
        sx={{
          paddingTop: 11,
          paddingBottom: 2,
          margin: "auto",
        }}
      ></TitleBox> */}
      <Box sx={{ marginTop: 12 }}></Box>
      <Box
        component="main"
        className="App"
        sx={{ display: { xs: "block", lg: "flex" } }}
      >
        <Container
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "column" },
            width: { xs: "100%" },
            maxWidth: { xs: "100%", lg: "600px" },
          }}
        >
          <Inputs
            setSelectedPairing={setSelectedPairing}
            selectedPairing={selectedPairing}
            datasetTimePairings={datasetTimePairings}
            setStageIdentified={setStageIdentified}
            stageIdentified={stageIdentified}
            stageList={stageList}
            setCountry={setCountry}
            country={country}
            countryList={countryList}
            setIsMulticenter={setIsMulticenter}
            isMulticenter={isMulticenter}
            multicenterList={multicenterList}
            setSelectedEnrollment={setSelectedEnrollment}
            selectedEnrollment={selectedEnrollment}
            enrollmentList={enrollmentList}
            setSelectedPhase={setSelectedPhase}
            selectedPhase={selectedPhase}
            phaseList={phaseList}
            handleTabChange={setCurrentTab}
            currentTab={currentTab}
            selectedRegistry={selectedRegistry}
            setSelectedRegistry={setSelectedRegistry}
            registryList={registryList}
            tabs={tabs}
          ></Inputs>
          <IntroductionAccordion></IntroductionAccordion>
        </Container>

        <Box
          sx={{
            flex: 1,
            marginLeft: 3,
            marginRight: 3,
            minHeight: "1200px",
            marginBottom: 2,
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : currentTab === tabs[0] ? (
            <Box
              sx={{
                height: Math.max(400, chartData.labels.length * 40 + 200),
                minHeight: "400px",
                maxHeight: "1600px",
              }}
            >
              <Bar options={createOptions(totalTrials)} data={chartData}></Bar>
            </Box>
          ) : currentTab === tabs[1] ? (
            <Box
              sx={{
                height: Math.max(
                  400,
                  stackedChartData.labels.length * 40 + 200
                ),
                minHeight: "400px",
                maxHeight: "1600px",
              }}
            >
              <Bar
                options={createStackedOptions(totalTrials)}
                data={stackedChartData}
              ></Bar>
            </Box>
          ) : (
            <Box>
              <Typography
                align="center"
                sx={{
                  marginTop: "5px",
                  marginBottom: 3,
                  fontWeight: "bold",
                  color: "#222",
                  fontSize: 20,
                  fontFamily: "Roboto",
                }}
              >
                Clinical trial results reporting by sponsor ({totalTrials}{" "}
                trials)
              </Typography>
              <DataGrid
                showToolbar
                sx={{ maxWidth: "1000px" }}
                rows={tableData}
                columns={dataGridColumns}
              ></DataGrid>
            </Box>
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default NordicDashboard;
